import { ReactNode, useContext } from "react";
import i18n from "../../language/i18n";
import { PenIcon, QuestionIcon } from "./PaneIcons";
import { usePaneFitMode } from "./usePaneFitMode";
import { ActivityQuestionLabelContext } from "./ActivityQuestionLabelContext";

export type ActivitySolveVariant =
  // No passage → QUESTION | ANSWER split, question-weighted (55/45). Short,
  // list-shaped answers: MCSA, MCMA.
  | "split"
  // No passage → QUESTION | ANSWER split, answer-weighted (40/60). Answers that
  // want the room: True/False, Open-ended, Matching, Grouping, Ordering.
  | "wide"
  // Full width, no split. The body carries in-sentence controls
  // (Dropdown/FillInTheBlanks); a body image floats so the sentence wraps it
  // (newspaper column). The answer section only renders when an interaction
  // (e.g. the word pool) exists.
  | "inline";

interface IActivitySolveLayoutProps {
  // Classic exam layout (user rule): READING PASSAGE on the LEFT (only when the
  // activity has one — an empty passage collapses the pane), QUESTION + ANSWER
  // on the RIGHT. With a passage the question and answer STACK (question above,
  // answer below) in the right column. WITHOUT a passage the question and answer
  // sit SIDE BY SIDE as two panes (Soru | Cevap) at the "split"/"wide" ratio.
  // "inline" is never split — it stays a single full-width column.
  variant: ActivitySolveVariant;
  // The question body (ActivityBodyContent). For the inline variant it also carries the
  // in-sentence controls.
  body: ReactNode;
  // The answer interaction (the per-type *MaterialContent), reused verbatim.
  interaction?: ReactNode;
  // Optional passage block for context-based activities → the LEFT pane.
  passage?: ReactNode;
  // Chat open / solution review → collapse the two-pane into a single stacked column.
  isFullScreen?: boolean;
  // OPT IN to pane pinning: when the two panes differ a lot in height, hold the
  // SHORTER one in view while the taller side scrolls (see usePaneFitMode).
  //
  // Deliberately defaults to OFF and is passed per template, because pinning is
  // only safe where the answer pane's height is STABLE while the learner works in
  // it. A pane that grows or shrinks mid-interaction re-measures and can flip
  // pinned↔not under the learner's cursor, moving the layout while they answer.
  // Safe today: MCSA, MCMA, True/False (selecting never resizes them) and
  // OpenEnded — being an input does not disqualify it, because `.oe2-area` is a
  // plain textarea with no auto-grow, so typing scrolls it internally instead of
  // growing the pane. OpenEnded is in fact the strongest case: a long stem beside
  // a ~200px answer box.
  // NOT wired, because these DO resize mid-interaction: Matching (a row expands
  // inline to pick a match), Grouping (items move from the pool into bins),
  // Ordering (also the only drag-and-drop template).
  //
  // Default-off is the point: a template added later cannot silently inherit
  // pinning — someone has to opt in and look at it.
  paneFit?: boolean;
  /**
   * OPT IN to a card-wide copy/cut guard: a handler that cancels the event (and tells
   * the learner why). Wired on the layout ROOT, so it covers the reading passage, the
   * question stem and the answer pane in one place — `copy`/`cut` fire on the element
   * holding the selection and bubble up here.
   *
   * Only OPEN_ENDED passes it today, and only while the question is being SOLVED —
   * the answer is prose the learner is supposed to write, so lifting the question out
   * to an external model is the cheat this blocks. Every other template is graded
   * against a key, so there is nothing to gain by copying it.
   *
   * The handler is INJECTED rather than implemented here on purpose: the toast lives in
   * `src/notifications`, which this self-contained library must not reach into.
   *
   * `contextmenu` is deliberately NOT routed through this — the right-click menu is also
   * spellcheck and undo for the answer textarea, and its Copy/Cut entries already fire
   * the events above.
   */
  copyGuard?: (event: any) => void;
  /**
   * Guard for a drag that starts inside the card. `dragstart` bubbles here from anywhere in
   * the card, and dragging a text SELECTION out to another tab is the same exfiltration a
   * blocked `copy` prevents — but some templates make their own elements `draggable` as the
   * ANSWER interaction (Ordering's rows). Cancelling those would break the question.
   *
   * So the drag is only refused when nothing in the ancestor chain opted into being
   * draggable. Structural rather than "only OPEN_ENDED passes copyGuard today", because that
   * is exactly how a template added later inherits a broken interaction silently.
   */
}

// Small labelled pill header (icon-in-rounded-box + uppercase label), matching the
// passage "OKUMA METNİ" card header — one for the question (body) and one for the
// answer (material).
const PaneHead = ({
  icon,
  label,
  tone,
}: {
  icon: ReactNode;
  label: string;
  tone?: "answer";
}) => {
  // The question pane carries the optional subject suffix; the answer pane never does.
  const questionSuffix = useContext(ActivityQuestionLabelContext);
  const title =
    tone !== "answer" && questionSuffix ? `${label} · ${questionSuffix}` : label;
  return (
    <div className="aas-pane-head">
      <span
        className={`aas-pane-ic${tone === "answer" ? " aas-pane-ic--answer" : ""}`}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="aas-pane-title">{title}</span>
    </div>
  );
};

const ActivitySolveLayout = ({
  variant,
  body,
  interaction,
  passage,
  isFullScreen,
  paneFit,
  copyGuard,
}: IActivitySolveLayoutProps) => {
  // Either pane can be the tall one, so both ask the same question and the answer
  // differs per pane: the SHORTER one pins while the taller side scrolls. Whether
  // the panes are actually side by side is decided by CSS (the 980px query,
  // --stacked, the :empty passage gate), so the hook reads the COMPUTED layout
  // rather than re-deriving it here. "inline" never splits, and a chat-expanded
  // (isFullScreen) layout stacks, so neither can pin.
  const paneFitEnabled = Boolean(paneFit) && variant !== "inline" && !isFullScreen;
  const [soruRef, soruPinned] = usePaneFitMode(paneFitEnabled);
  const [answerRef, answerPinned] = usePaneFitMode(paneFitEnabled);
  const pinClass = (isPinned: boolean) => (isPinned ? " aas-pane--pin" : "");

  // Question and answer. "inline" keeps its full-width single-column shape
  // (in-sentence controls + optional answer section). The split variants wrap
  // the question in .aas-soru so it can sit BESIDE the .aas-answers pane when
  // there's no passage (see the Soru | Cevap rules in activitySolveLayoutStyles).
  const content =
    variant === "inline" ? (
      <>
        <PaneHead icon={<QuestionIcon />} label={i18n.t("question")} />
        <div className="aas-inline-body">{body}</div>
        {interaction ? (
          <div className="aas-answers">
            <PaneHead icon={<PenIcon />} label={i18n.t("answer")} tone="answer" />
            {interaction}
          </div>
        ) : null}
      </>
    ) : (
      // .aas-pane-inner is what makes pinning possible: the PANE is stretch-sized
      // to the taller sibling while this block keeps its content height, and that
      // gap is the only room a sticky element has to travel in. Without it the
      // pane fills itself exactly and `position: sticky` is a silent no-op.
      // The header lives INSIDE it so a pinned pane keeps its SORU / CEVAP label.
      // The inline branch above has no beside-pane, so it is left untouched.
      <>
        <div className={`aas-soru${pinClass(soruPinned)}`} ref={soruRef}>
          <div className="aas-pane-inner">
            <PaneHead icon={<QuestionIcon />} label={i18n.t("question")} />
            <div className="aas-body">{body}</div>
          </div>
        </div>
        <div className={`aas-answers${pinClass(answerPinned)}`} ref={answerRef}>
          <div className="aas-pane-inner">
            <PaneHead icon={<PenIcon />} label={i18n.t("answer")} tone="answer" />
            {interaction}
          </div>
        </div>
      </>
    );

  const gridClass = `aas-grid ${isFullScreen ? "aas-grid--stacked" : ""}`;
  const layoutClass = `aas-layout ${!isFullScreen ? "aas-layout--panes" : ""}`;
  // The variant modifier drives the no-passage Soru | Cevap split ratio (or
  // opts out of it, for inline).
  const contentColClass = `aas-content-col aas-content-col--${variant}`;

  // The passage node is passed unconditionally by the solve screens and renders
  // null when the activity has no passage — .aas-passcol:empty collapses the
  // pane (and switches the content column from stacked to a Soru | Cevap split).
  // See the copyGuard doc comment: refuse a selection drag out of the card, but never a
  // template's own drag-to-answer interaction.
  const handleDragStart = (event: any) => {
    if (!copyGuard) return;
    const target = event?.target;
    if (target && typeof target.closest === "function") {
      if (target.closest('[draggable="true"]')) return;
    }
    copyGuard(event);
  };

  return (
    <div
      className={layoutClass}
      onCopy={copyGuard}
      onCut={copyGuard}
      onDragStart={copyGuard ? handleDragStart : undefined}
    >
      <div className={gridClass}>
        {passage ? <div className="aas-passcol">{passage}</div> : null}
        <div className={contentColClass}>{content}</div>
      </div>
    </div>
  );
};

export default ActivitySolveLayout;
